import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import type { ThemeMode } from "../types";
import { useWorkspaceThemeMode } from "../hooks/useWorkspaceThemeMode";

const THEME_STORAGE_KEY = "budget-trace-theme";

function getStoredThemeMode(): ThemeMode {
  if (typeof window === "undefined") return "light";
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "dark" || stored === "light") return stored;
  } catch {
    // ignore
  }
  return "light";
}

export interface ThemeContextValue {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useThemeMode(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useThemeMode must be used within ThemeModeProvider");
  }
  return ctx;
}

export function ThemeModeProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(getStoredThemeMode);

  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
    document.documentElement.classList.toggle("dark", mode === "dark");
    document.documentElement.style.colorScheme = mode === "dark" ? "dark" : "light";
    if (typeof document !== "undefined" && document.body) {
      document.body.style.backgroundColor = mode === "dark" ? "#121212" : "#F9FAFB";
    }
    try {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch {
      // ignore
    }
    // iPhone status bar / browser chrome: dark bar + white time & icons in dark mode
    if (typeof document !== "undefined") {
      const themeColorMeta = document.querySelector('meta[name="theme-color"]');
      if (themeColorMeta) themeColorMeta.setAttribute("content", mode === "dark" ? "#121212" : "#FFFFFF");
      const appleBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
      if (appleBarMeta) appleBarMeta.setAttribute("content", mode === "dark" ? "black" : "default");
    }
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", themeMode === "dark");
    document.documentElement.style.colorScheme = themeMode === "dark" ? "dark" : "light";
    if (typeof document !== "undefined" && document.body) {
      document.body.style.backgroundColor = themeMode === "dark" ? "#121212" : "#F9FAFB";
    }
    // Keep iPhone status bar / browser chrome in sync with theme
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) themeColorMeta.setAttribute("content", themeMode === "dark" ? "#121212" : "#FFFFFF");
    const appleBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    if (appleBarMeta) appleBarMeta.setAttribute("content", themeMode === "dark" ? "black" : "default");
  }, [themeMode]);

  const value: ThemeContextValue = {
    themeMode,
    setThemeMode,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Renders nothing. When mounted with a workspaceId, syncs theme from that workspace's
 * settings to the global theme (and persists to localStorage). Only updates when we have
 * received the workspace theme from Firestore (not on first render), so we avoid a flash
 * from stored dark -> default light -> dark after load.
 */
export function ThemeSync({ workspaceId }: { workspaceId: string }): null {
  const { setThemeMode } = useThemeMode();
  const themeModeFromWorkspace = useWorkspaceThemeMode(workspaceId);

  useEffect(() => {
    if (themeModeFromWorkspace !== null) {
      setThemeMode(themeModeFromWorkspace);
    }
  }, [workspaceId, themeModeFromWorkspace, setThemeMode]);

  return null;
}
